/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Icon, Popup } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { flattenObject } from "services/utils";
import { DefaultColumnFilter, SelectColumnFilter, Table } from "../Table/Table";
import LoadResolver from "components/LoadResolver";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { formatDate, getUserNameEmailString, handleDownloadClick } from "services/common";
import { getAssetsTableState, getUsers } from "store/selectors";
import AssetImage from "components/AssetImage/AssetImage";

function AssetTable({ dispatch, t, isAdmin = false }) {
  useEffect(() => {
    dispatch.assets.loadLazy();
  }, []);

  const usersList = useSelector(getUsers);

  let { isLoading, isLazyLoading, error, items } = useSelector((state) => state.assets);
  const { filterValues, globalFilterValue, isRemovingFilters } = useSelector(getAssetsTableState);
  const hasFilters = useMemo(() => filterValues.filter(({ value }) => value !== ""), [
    filterValues,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      isRemovingFilters && dispatch.reactTableAssets.resetFilters();
    }, 100);
    return () => clearTimeout(timer);
  }, [isRemovingFilters]);

  return (
    <LoadResolver isLoading={isLoading || usersList.isLoading} error={error} hideWhenLoading>
      <div style={{ marginBottom: "-2.6em", display: "flex", gap: "1rem" }}>
        <Popup
          content={t("asset_reload_assets")}
          trigger={
            <Button
              icon
              onClick={() => dispatch.assets.loadLazy({ force: true })}
              disabled={isLazyLoading}
            >
              <Icon name="redo alternate" />
            </Button>
          }
          position="top right"
        />
        <Button
          disabled={hasFilters?.length <= 0 && !globalFilterValue}
          basic
          className="bmo-purple"
          onClick={() => {
            dispatch.reactTableAssets.setIsRemovingFilters();
          }}
        >
          <Icon name="remove" />
          {t("campaigns_page_clear_filters")}
        </Button>
      </div>

      <DeliveriesList
        assets={items}
        dispatch={dispatch}
        hasFilters={hasFilters}
        isAdmin={isAdmin}
        usersList={usersList}
        t={t}
        isLazyLoading={isLazyLoading}
      />
    </LoadResolver>
  );
}

const RenderMedia = ({ asset, t }) => {
  return (
    <Popup
      pinned
      position="right center"
      content={t("asset_click_to_download")}
      trigger={
        <a
          style={{ display: "flex", justifyContent: "center" }}
          href={asset.mediaUrl}
          onClick={(e) => handleDownloadClick(e, asset.mediaUrl, t)}
        >
          <AssetImage asset={asset} type={asset.assetType} size="huge" />
        </a>
      }
    />
  );
};

function DeliveriesList(props) {
  const { assets: assetsProp, dispatch, isAdmin, t, isLazyLoading, usersList } = props;
  const { filterValues, globalFilterValue, isRemovingFilters } = useSelector(getAssetsTableState);
  const skipPageResetRef = useRef();
  const assets = useMemo(() => {
    skipPageResetRef.current = true;
    const newData = assetsProp.map((obj) => {
      const flattenedData = flattenObject(obj);
      const renameAssetsData = { ...flattenedData };

      const assetSender = usersList?.items?.filter((user) => user.id === obj.owner);

      renameAssetsData.assetName = obj?.name ?? "";
      renameAssetsData.campaignName = obj?.campaign?.name ?? "";
      renameAssetsData.campaignContractNumber = obj?.campaign?.contract_number ?? "";
      renameAssetsData.assetType = (obj?.type || "").split("/")[1] || obj?.type || "";
      renameAssetsData.assetStatus = (obj?.status || "").replace("_", " ");
      renameAssetsData.assetUpdatedAt = obj?.updatedAt ?? "";
      renameAssetsData.assetCreatedAt = obj?.createdAt ?? "";
      renameAssetsData.assetNotes = obj?.notes ?? "";
      renameAssetsData.assetSender = assetSender?.[0] ? getUserNameEmailString(assetSender[0]) : "";

      return renameAssetsData;
    });

    return newData;
  }, [assetsProp]);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState({
    text: "",
    headerText: "",
    icon: "",
  });
  const onFilterChange = (value, id) => {
    dispatch.reactTableAssets.addOrUpdateFilterValue({ value: value, id: id });
  };
  const setGlobalFilterValue = (value) => {
    dispatch.reactTableAssets.setGlobalFilterValue(value);
  };
  const [confirmationModalDataForOnSuccess, setConfimationModalDataForOnSuccess] = useState({});

  const deleteAsset = (asset) => {
    dispatch.assets.delete(asset);
  };

  const columns = useMemo(
    () => [
      {
        Header: t("asset_media"),
        Cell: function MediaCell({ row }) {
          return <RenderMedia asset={row.original} t={t} />;
        },
      },
      {
        Header: t("asset_name"),
        accessor: "assetName",
        Cell: function NameRow({ row }) {
          return (
            <Popup
              content={row.original.assetName}
              trigger={<p style={{ width: "max-content" }}>{row.original.assetName}</p>}
            />
          );
        },
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        width: 220,
      },
      {
        Header: t("asset_campaign"),
        accessor: "campaignName",
        Cell: function CampaignRow({ row }) {
          const value = row.original.campaignName || "-";
          return (
            <Popup
              content={value === "-" ? "" : value}
              trigger={<p style={{ width: "max-content", textAlign: "center" }}>{value}</p>}
            />
          );
        },
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        width: 220,
      },
      {
        Header: t("asset_status"),
        accessor: "assetStatus",
        filter: "select",
        Filter: function selectFilter(props) {
          return (
            <SelectColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("asset_resolution"),
        accessor: "resolution",
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("asset_type"),
        accessor: "assetType",
        filter: "select",
        Filter: function selectFilter(props) {
          return (
            <SelectColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        selectSplit: "1",
      },
      {
        Header: t("asset_createdAt"),
        accessor: "assetCreatedAt",
        Cell: function dateCell({ row }) {
          return formatDate(row.original.assetCreatedAt);
        },
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("asset_updatedAt"),
        accessor: "assetUpdatedAt",
        Cell: function dateCell({ row }) {
          return formatDate(row.original.assetUpdatedAt);
        },
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
      },
      {
        Header: t("campaign_contract_number"),
        accessor: "campaignContractNumber",
        Filter: function filter(props) {
          return (
            <DefaultColumnFilter
              {...props}
              t={t}
              onFilterChange={(value, id) => onFilterChange(value, id)}
            />
          );
        },
        width: 240,
      },
      {
        Header: "Sender",
        accessor: "assetSender",
        id: "assetSender",
      },
      {
        id: "updateDeleteRow",
        Cell: function updateDeleteRow({ row }) {
          const asset = row.original;
          return (
            <>
              <Popup
                content={t("to_campaign_page")}
                trigger={
                  <Button
                    basic
                    icon
                    size="mini"
                    as={Link}
                    to={(isAdmin ? "/campaigns/" : "/deliveries/") + asset.campaignID}
                    disabled={!asset.campaignID ? true : false}
                  >
                    <Icon name="edit outline" />
                  </Button>
                }
                position="top right"
              />
              <Popup
                content={asset.campaignID ? t("asset_cannot_delete_message") : t("asset_delete")}
                trigger={
                  <div style={{ display: "inline-block" }}>
                    <Button
                      data-test="asset_delete_button"
                      basic
                      icon
                      size="mini"
                      onClick={() => {
                        setConfirmationModalData({
                          text: t("asset_delete_modal_text"),
                          headerText: t("asset_delete"),
                          icon: "delete",
                        });
                        setConfimationModalDataForOnSuccess(row.original);
                        setConfirmationModalOpen(true);
                      }}
                      disabled={asset.campaignID ? true : false}
                    >
                      <Icon name="delete" />
                    </Button>
                  </div>
                }
                position="top right"
              />
            </>
          );
        },
      },
    ],
    [t]
  );

  return (
    <div className="page-container">
      <Table
        columns={columns}
        hiddenColumns={["assetSender"]}
        data={assets}
        useTableGlobalFilter={true}
        manualFilters={true}
        skipPageResetRef={skipPageResetRef}
        autoResetFilters={false}
        t={t}
        isLazyLoading={isLazyLoading}
        tableClass="styled-table"
        defaultFilteredFields={filterValues}
        defaultGlobalFieldValue={globalFilterValue}
        setGlobalFilterValue={(value) => setGlobalFilterValue(value)}
        isRemovingFilters={isRemovingFilters}
      />
      <ConfirmationModal
        data={confirmationModalData}
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        onSuccess={(asset) => deleteAsset(asset)}
        dataForOnSuccess={confirmationModalDataForOnSuccess}
      />
    </div>
  );
}

export default AssetTable;
